import React, { useMemo } from 'react';
import {
  Autocomplete,
  Checkbox,
  FormControlLabel,
  FormGroup,
  TextField,
  Typography,
} from '@mui/material';

const CHECKBOX_THRESHOLD = 15;

/**
 * Múltipla escolha genérica (lista estática de strings).
 * - Até ~15 opções: checkboxes empilhados
 * - Acima: Autocomplete multiple
 *
 * value: string[]
 */
export default function SelecaoMultiplaCheckbox({
  options = [],
  value = [],
  onChange,
  disabled = false,
  label = 'Selecione',
}) {
  const items = useMemo(
    () => (Array.isArray(options) ? options.map((o) => String(o)).filter(Boolean) : []),
    [options]
  );
  const selected = Array.isArray(value) ? value.map(String) : [];

  const toggle = (opt) => {
    if (disabled) return;
    const next = selected.includes(opt)
      ? selected.filter((x) => x !== opt)
      : [...selected, opt];
    onChange(next);
  };

  if (!items.length) {
    return (
      <Typography variant="caption" color="text.secondary">
        Nenhuma opção configurada.
      </Typography>
    );
  }

  if (items.length <= CHECKBOX_THRESHOLD) {
    return (
      <FormGroup>
        {items.map((opt) => (
          <FormControlLabel
            key={opt}
            control={
              <Checkbox
                size="small"
                disabled={disabled}
                checked={selected.includes(opt)}
                onChange={() => toggle(opt)}
              />
            }
            label={opt}
          />
        ))}
      </FormGroup>
    );
  }

  return (
    <Autocomplete
      multiple
      disabled={disabled}
      options={items}
      value={selected}
      onChange={(_, next) => onChange(next || [])}
      disableCloseOnSelect
      renderOption={(props, option, { selected: isSel }) => (
        <li {...props}>
          <Checkbox size="small" style={{ marginRight: 8 }} checked={isSel} />
          {option}
        </li>
      )}
      renderInput={(params) => (
        <TextField {...params} size="small" label={label} placeholder="Buscar…" />
      )}
    />
  );
}

/** Serializa array de strings (ou objetos) para text_value. */
export function serializeMultiSelection(selected) {
  if (!selected?.length) return '';
  if (typeof selected[0] === 'string') {
    return JSON.stringify(selected);
  }
  return JSON.stringify(selected);
}

export function parseMultiSelection(textValue) {
  if (!textValue) return [];
  try {
    const parsed = JSON.parse(textValue);
    if (Array.isArray(parsed)) {
      return parsed.map((s) => (typeof s === 'string' ? s : s?.label || String(s)));
    }
  } catch {
    /* valor legado único */
    return [String(textValue)];
  }
  return [];
}

export function getQuestionSelectionMode(question) {
  if (!question || question.responseType !== 'selecao') return null;
  if (question.selectionMode === 'multiple' || question.selectionMode === 'single') {
    return question.selectionMode;
  }
  const opts = question.options;
  if (opts && typeof opts === 'object' && !Array.isArray(opts)) {
    if (opts.source === 'contratos') return opts.selectionMode === 'single' ? 'single' : 'multiple';
    if (opts.selectionMode === 'multiple' || opts.multiple) return 'multiple';
  }
  return 'single';
}

export function getManualSelecaoItems(question) {
  const opts = question?.options;
  if (Array.isArray(opts)) return opts.map(String);
  if (opts && typeof opts === 'object' && Array.isArray(opts.items)) {
    return opts.items.map(String);
  }
  return [];
}

export function isContratosOptions(options) {
  return options && typeof options === 'object' && !Array.isArray(options) && options.source === 'contratos';
}
